import Page from "@/containers/Page";
import { Col, Div, Row } from "@/styles/layout";
import { usePageInfo } from "@/hooks/usePageInfo";
import { useEffect, useMemo, useState } from "react";
import Card from "@/containers/Card";
import { useAccount } from "@/hooks/useAccount";
import { Body } from "@/styles/typography";
import { toast } from "react-hot-toast";
import EmbeddableCode from "@/containers/EmbedInstructions";
import MyButton from "@/components/Button";
import { FiExternalLink } from "react-icons/fi";
import Input from "@/components/Input";
import { BsWordpress } from "react-icons/bs";
import { FaShopify, FaSquarespace, FaWeebly, FaWix } from "react-icons/fa";
import { IconType } from "react-icons";
import { Account } from "@/types/models/Account";
import { AiOutlineCopy } from "react-icons/ai";

const Instruction = ({
  title,
  Icon,
  link,
}: {
  title: string;
  Icon: IconType;
  link?: string;
}) => {
  const handleClick = () => {
    if (!link) {
      toast.error(
        "Coming soon... Contact us at support@gogive.com.au to get started with your installation."
      );
    }
  };
  return (
    <div onClick={handleClick}>
      <a href={link} target="_blank" style={{ textDecoration: "none" }}>
        <Row
          m="16px"
          alignItems="center"
          p="16px"
          color="primary.textHigh"
          borderRadius="4px"
          boxShadow={"m"}
        >
          <Icon size={54} />
          <Col ml="16px">
            <Body fontSize="18px" fontWeight="600">
              {title}
            </Body>
          </Col>
        </Row>
      </a>
    </div>
  );
};

const BUILDERS = [
  {
    title: "WordPress",
    Icon: BsWordpress,
    link: "https://gogive.com.au/support/wordpress",
  },
  {
    title: "Wix",
    Icon: FaWix,
    link: "https://gogive.com.au/support/wix",
  },
  {
    title: "Squarespace",
    Icon: FaSquarespace,
  },
  {
    title: "Shopify",
    Icon: FaShopify,
    link: "https://gogive.com.au/support/shopify",
  },
  {
    title: "Weebly",
    Icon: FaWeebly,
  },
];

export default function Installation() {
  const routeInfo = usePageInfo();
  const { account, refresh } = useAccount();

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={routeInfo.name!}
      Icon={routeInfo.icon!}
    >
      <Div display="grid" gridTemplateColumns={"1fr"} gridGap="24px">
        <Card
          header={`Installation Code`}
          description="Copy this code and add it to your website's header"
        >
          <EmbeddableCode />
        </Card>
        <Card
          header={`Instructions`}
          description="Click on your website builder to access the installation instructions."
        >
          <Div
            display="grid"
            gridTemplateColumns={"repeat(auto-fit, minmax(400px,1fr)) "}
            gridGap="24px"
          >
            {BUILDERS.map((b) => (
              <Instruction
                key={b.title}
                title={b.title}
                Icon={b.Icon}
                link={b.link}
              />
            ))}
          </Div>
        </Card>
      </Div>
    </Page>
  );
}
function removeDoubleSlashes(url: string): string {
  const doubleSlashRegex = /([^:/])(\/{2,})/g;

  const cleanedUrl = url.replace(doubleSlashRegex, "$1/");
  return cleanedUrl;
}

const DonationLinkBuilder = ({ account }: { account: Account | undefined }) => {
  const [root, setRoot] = useState<string>(account?.website ?? "");
  const [page, setPage] = useState<string>("");

  const link = useMemo(
    () => removeDoubleSlashes(`${root}/${page}?show-form=true`),
    [page, root]
  );

  useEffect(() => {
    if (account?.website) {
      setRoot(account.website);
    }
  }, [account?.website]);

  return (
    <Col mt="16px">
      <Row>
        <Input
          label="Website Address"
          value={root}
          containerStyle={{ width: 500, marginBottom: 44, marginRight: 16 }}
          onChange={setRoot}
        />
        <Input
          label="Page (optional)"
          value={page}
          containerStyle={{ width: 500, marginBottom: 44 }}
          onChange={setPage}
        />
      </Row>
      <Col alignItems="center">
        <Col
          alignItems="center"
          justifyContent={"center"}
          bg="primary.element"
          borderRadius="4px"
          p="24px"
        >
          <Col mb="16px">
            <Body
              fontSize="16px"
              fontWeight="500"
              style={{ whiteSpace: "nowrap" }}
              color="primary.textHigh"
            >
              {link}
            </Body>
          </Col>
          <Row>
            <MyButton
              variant="outline"
              Icon={AiOutlineCopy}
              label="Copy"
              onClick={() => {
                navigator.clipboard.writeText(link);
                toast.success("Link copied!");
              }}
              containerStyle={{ borderRadius: 8, px: 24, mr: 16 }}
              iconSize={18}
              labelStyle={{ fontSize: "16px" }}
            />
            <MyButton
              variant="outline"
              Icon={FiExternalLink}
              label="Visit"
              onClick={() => {
                window.open(link, "_blank");
              }}
              containerStyle={{ borderRadius: 8, px: 24, mr: 16 }}
              iconSize={18}
              labelStyle={{ fontSize: "16px" }}
            />
          </Row>
        </Col>
      </Col>
    </Col>
  );
};
